import React from 'react';
import { Link, useLocation } from 'react-router';
import { Box } from '@mui/material';
import { IExtractionBatch } from '../../types/my-types';
import ExtractionBatchReTransform from './actions/ExtractionBatchReTransform';
import ExtractionBatchDownloadModal from './actions/ExtractionBatchDownloadModal';
import ExtractionBatchSend from './actions/ExtractionBatchSend';
import ExtractionBatchReExtract from './actions/ExtractionBatchReExtract';
import ExtractionBatchDelete from './actions/ExtractionBatchDelete';
import ExtractionBatchClean from './actions/ExtractionBatchClean';
import ExtractionBatchNameAndTS from './ExtractionBatchNameAndTS';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  DivVerticalDividerLarge,
  DivVerticalDividerSmall,
  DivFlexHeaderIcons,
  DivFlexHeader,
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../common/StyledComponents';
import ButtonGlynt from '../common/Buttons/ButtonGlynt';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { CORE_VERSION } from '../../constants';

import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { AppContext } from '../../App';

interface IProps {
  extractionBatch: IExtractionBatch | undefined;
  dpid: string;
  eid: string;
}

const ExtractionBatchHeader = ({ extractionBatch, dpid, eid }: IProps) => {
  const cache = useQueryClient();
  const location = useLocation();
  const { setToast } = React.useContext(AppContext);

  const selectedTab = React.useMemo(() => {
    const p = location.pathname;
    if (p.includes('/extraction-batch/') && p.includes('/transformed')) return 'transformed';
    if (p.includes('/extraction-batch/') && p.includes('/review/')) return 'review';
    if (p.includes('/extraction-batch/') && p.includes('/details')) return 'details';

    // old transformed route no longer needed; keep review fallback for legacy deep links
    if (p.includes('/review/') && p.includes('/extraction/')) return 'review';
    return 'details';
  }, [location.pathname]);

  const afterActionSuccess = () => {
    setTimeout(() => {
      cache.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      cache.invalidateQueries({
        queryKey: ['extractionBatch']
      });
      cache.invalidateQueries({
        queryKey: ['allDocuments']
      });
      cache.invalidateQueries({
        queryKey: ['allExtractions']
      });
    }, 3000);
  };

  // Extract the first extraction ID for the review URL
  const firstExtractionId = React.useMemo(() => {
    return extractionBatch && extractionBatch.extractions && extractionBatch.extractions.length > 0
      ? getIdFromUrl(extractionBatch.extractions[0])
      : '';
  }, [extractionBatch]);

  return (
    <>
      <DivFlexHeader>
        <ExtractionBatchNameAndTS
          extractionBatch={extractionBatch}
          ebCharLimit={35}
          dpid={dpid}
          eid={eid}
        />

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {extractionBatch ? (
            <DivFlexHeaderIcons>
              <div>
                <TypographyPropertiesTitleStyled>ID</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  <span
                    onClick={() => {
                      navigator.clipboard.writeText(extractionBatch.id || '');
                      setToast({
                        open: true,
                        message: 'Copied!',
                        variant: 'success',
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {extractionBatch.id}
                  </span>
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />

              <div>
                <TypographyPropertiesTitleStyled>EXTRACTION</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {extractionBatch.status}
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />

              <div>
                <TypographyPropertiesTitleStyled>TRANSFORMATION</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {extractionBatch.data_processing_status}
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />
              <div>
                <TypographyPropertiesTitleStyled>UPDATED AT</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {format(new Date(extractionBatch?.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />

              <ExtractionBatchClean
                extractionBatch={extractionBatch}
                afterActionSuccess={afterActionSuccess}
                type="icon"
              />
              <ExtractionBatchReTransform
                extractionBatch={extractionBatch}
                afterActionSuccess={afterActionSuccess}
                type="icon"
              />
              <ExtractionBatchDownloadModal
                extractionBatch={extractionBatch}
                ebFinished={extractionBatch?.finished}
                type="icon"
              />
              <ExtractionBatchReExtract
                extractionBatch={extractionBatch}
                coreVersion={CORE_VERSION}
                type="icon"
              />
              <ExtractionBatchSend
                extractionId={extractionBatch.id}
                ebLabel={extractionBatch.label}
                ebFinished={extractionBatch.finished}
                isExpired={extractionBatch.verified?.expired}
                isVerified={Boolean(extractionBatch.verified?.is_verified)}
                verifiedUpdatedAt={extractionBatch.verified?.updated_at}
                type="icon"
              />
              <ExtractionBatchDelete extractionBatchId={extractionBatch.id} type="icon" />
            </DivFlexHeaderIcons>
          ) : null}
        </div>
      </DivFlexHeader>
      <Box sx={{ mt: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Tabs
          value={selectedTab}
          aria-label="Extraction batch sections"
          role="tablist"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              px: 2,
              py: 0.75,
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              color: '#5f6d7e',
              letterSpacing: '0.2px',
              '&:hover': {
                color: '#1a2332',
              },
              '&.Mui-selected': {
                color: '#1D9AAA',
                fontWeight: 600,
              },
              '&.Mui-disabled': {
                opacity: 0.4,
              },
            },
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              backgroundColor: '#1D9AAA',
            },
          }}
        >
          <Tab
            label="Details"
            value="details"
            component={Link}
            to={`/data-pools/${dpid}/extraction-batch/${eid}/details`}
            aria-controls="tab-eb-details"
            data-cy="tab-eb-details"
          />
          <Tab
            label="Review"
            value="review"
            component={Link}
            to={`/data-pools/${dpid}/extraction-batch/${eid}/review/${firstExtractionId}`}
            aria-controls="tab-panel-review"
            data-cy="tab-eb-review"
            disabled={
              !extractionBatch?.extractions ||
              extractionBatch.extractions.length === 0 ||
              !firstExtractionId
            }
          />
          <Tab
            label="Transformed"
            value="transformed"
            component={Link}
            to={`/data-pools/${dpid}/extraction-batch/${eid}/transformed`}
            aria-controls="tab-panel-transformed"
            data-cy="tab-eb-transformed"
          />
        </Tabs>
      </Box>
    </>
  );
};
export default ExtractionBatchHeader;
